// R-131-FIN-DECISION-A — gas combined-cycle pathway preset (NREL ATB / EIA AEO).

import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import { DEFAULT_GAS_FUEL_PRICE_CAGR_PCT } from '../../inflationBasis';
import type { Pathway } from '../types';
import {
  DEFAULT_BASE_WACC_PCT,
  annualMwhFromCf,
  buildCapexBlock,
  buildStandardBtmPhases,
  capexFromPerKw,
  computeAnnualOpexUsd,
  mergePathway,
  pathwayIdFromTechnology,
  resolveFacilityLoadMw,
  resolveNameplateMw,
  resolveProjectStartDate,
  valueOfServedLoadRevenue,
  type PathwayPresetOptions,
} from './shared';

/** NREL ATB 2024 / EIA AEO 2024 — F-class CCGT, no CCUS. */
export const GAS_CC_BENCHMARKS = {
  capexPerKwUsd: 1100,
  fixedOmPerKwYearUsd: 16,
  variableOmPerMwhUsd: 4,
  fuelCostPerMwhUsd: 35,
  designLifeYears: 25,
  availabilityPct: 92,
  capacityFactorPct: 85,
  constructionMonths: 36,
  riskPremiumBps: 75,
  fuelPriceCagrPct: DEFAULT_GAS_FUEL_PRICE_CAGR_PCT,
  fuelPriceStdev: 4,
  tCo2ePerMwh: 0.4,
  decommPctOfCapex: 0.05,
  contingencyPct: 10,
} as const;

export function presetFor(
  intent: ProjectIntent,
  _siteContext: SiteContext,
  options?: PathwayPresetOptions,
): Pathway {
  // Generation NAMEPLATE: the facility LOAD grossed up to a plant that can
  // SERVE the firm 24/7 load (gas-cc ≈ load × 1.25). Capex / energy / O&M ride
  // this nameplate; the load is carried separately for load-tracking costs.
  const facilityLoadMw = resolveFacilityLoadMw(intent);
  const capacityMw = resolveNameplateMw(intent, 'gas-cc');
  const startDate = resolveProjectStartDate(intent);
  const b = GAS_CC_BENCHMARKS;

  const baseCapexUsd = capexFromPerKw(capacityMw, b.capexPerKwUsd);
  const decommissioningUsd = baseCapexUsd * b.decommPctOfCapex;
  const annualMwh = annualMwhFromCf(capacityMw, b.capacityFactorPct);
  const annualOpexUsd = computeAnnualOpexUsd(
    capacityMw,
    b.fixedOmPerKwYearUsd,
    b.variableOmPerMwhUsd,
    b.fuelCostPerMwhUsd,
    annualMwh,
  );

  const capex = buildCapexBlock(startDate, b.constructionMonths, baseCapexUsd, {
    contingencyPct: b.contingencyPct,
  });

  const codYear =
    new Date(startDate).getUTCFullYear() + Math.ceil(b.constructionMonths / 12);

  const base: Pathway = {
    id: pathwayIdFromTechnology('gas-cc'),
    name: 'BTM Gas Combined Cycle',
    classification: 'btm',
    technology: 'gas-cc',
    capacityMW: capacityMw,
    facilityLoadMW: facilityLoadMw,
    phases: buildStandardBtmPhases({
      startDate,
      constructionMonths: b.constructionMonths,
      designLifeYears: b.designLifeYears,
      totalCapexUsd: baseCapexUsd,
      decommissioningUsd,
      annualMwh,
      annualOpexUsd,
    }),
    capex,
    opex: {
      fixedOmPerKwYearUsd: b.fixedOmPerKwYearUsd,
      variableOmPerMwhUsd: b.variableOmPerMwhUsd,
      fuelCostPerMwhUsd: b.fuelCostPerMwhUsd,
      fuelPriceCagrPct: b.fuelPriceCagrPct,
      fuelPriceStdev: b.fuelPriceStdev,
    },
    // VALUE-CONSISTENT credit — same value-of-served-load as every other
    // pathway (incl. grid) so the compare-NPV is apples-to-apples. See
    // valueOfServedLoadRevenue / VALUE_OF_SERVED_LOAD_USD_PER_MWH in shared.ts.
    revenue: valueOfServedLoadRevenue({
      termYears: b.designLifeYears,
      availabilityPct: b.availabilityPct,
    }),
    risk: {
      technologyMaturity: 'mature',
      regulatoryRisk: 'low',
      fuelPriceRisk: 'high',
      baseWaccPct: DEFAULT_BASE_WACC_PCT,
      riskPremiumBps: b.riskPremiumBps,
    },
    carbon: {
      tCo2ePerMwh: b.tCo2ePerMwh,
    },
    lifetime: {
      designLifeYears: b.designLifeYears,
      decommissioningUsd,
      decommissioningYear: codYear + b.designLifeYears,
    },
  };

  return mergePathway(base, options?.overrides);
}
